import { z } from 'zod';
import { COMMON_STRINGS } from '@/lib/constants/common-strings';

const MESSAGE = COMMON_STRINGS.VALIDATION.ENTER_A_NUMBER;
const DIGITS_ONLY = /^\d+$/;
const DECIMAL_DIGITS = /^\d+\.\d+$/;

/**
 * Blank (`''`, `null`, `undefined`) becomes `undefined`; a digit string becomes its number; a
 * non-negative decimal (a value saved before the numeric input existed, or a restored draft) is
 * ROUNDED to whole units; anything else is passed through untouched so the number check below
 * rejects it with the plain message instead of a validation-library default.
 */
function toWholeNumberInput(value: unknown): unknown {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 ? Math.round(value) : value;
  }
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  if (DIGITS_ONLY.test(trimmed)) return Number(trimmed);
  return DECIMAL_DIGITS.test(trimmed) ? Math.round(Number(trimmed)) : value;
}

const wholeNumber = z
  .number({ error: MESSAGE })
  .int(MESSAGE)
  .nonnegative(MESSAGE)
  .safe(MESSAGE);

/**
 * Optional whole-number field for zod form schemas (money amounts, counts, ids). Accepts the
 * plain digit string the numeric `TextField` holds, or a number from a saved profile (a decimal is
 * rounded to whole units). Blank parses to `undefined`; anything else that is not a number >= 0 fails
 * with "Enter a number".
 * Replaces the per-form `optionalNumber` copies (MYN-277).
 */
export const optionalWholeNumber = z.preprocess(toWholeNumberInput, wholeNumber.optional());

/** `optionalWholeNumber` for a field that must be filled: blank also fails with "Enter a number". */
export const requiredWholeNumber = z.preprocess(toWholeNumberInput, wholeNumber);
